import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { useStaticQuery, graphql } from "gatsby";
import React, { useRef, useReducer, useEffect, useCallback } from "react";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { isEmpty } from "lodash";

import IconOnlyButton from "~/components/buttons/IconOnlyButton";
import Button from "~/components/buttons/Button";
import ContentContainer from "~/components/layout/ContentContainer";
import BaseLink from "~/components/links/BaseLink";
import ImageLink from "~/components/links/ImageLink";
import ShoppingCart from "~/components/links/ShoppingCart";

import ErrorBoundary from "~/components/ErrorBoundary";
import HeaderSearch from "~/components/HeaderSearch";
import LanguageDropdown from "~/components/LanguageDropdown";
import MainMobileNavigationItem from "~/components/MainMobileNavigationItem";
import MainNavigation from "~/components/MainNavigation";
import MobileLanguageNav from "~/components/MobileLanguageNav";

import {
    isBrowser,
    KEYCODE_ESCAPE,
    KEYCODE_TAB,
    scrollToElement,
} from "~/utilities/helpers";

import transition from "~/utilities/transition";
import {
    adjustNavMenuFocus,
    navigateMenuWithKeyboard,
} from "~/utilities/headerHelpers";

import { initHeaderState, headerReducer } from "~/reducers/header";

import useOnWindowResize from "~/hooks/useOnWindowResize";
import useOnKeyPress from "~/hooks/useOnKeyPress";
import useGetBasePath from "~/hooks/useGetBasePath";
import useStringTranslation from "~/hooks/translations/useStringTranslation";

import { screenReaderText } from "~/styles/screenReaderText";
import trackNavigationEvent from "~/utilities/trackNavigationEvent";

const HeaderStyled = styled.header`
    position: relative;
    z-index: ${({ theme }) => theme.zIndex.header};
    background-color: ${({ theme }) =>
        theme.colors.backgroundColor.defaultLight};

    @media (max-width: ${({ theme }) => theme.breakpoints.desktopMax}) {
        padding: 25px 0;
    }
`;

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const Logo = styled.div`
    margin-right: 15px;
    max-width: 60vw;

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        max-width: 100%;
    }
`;

const MobileNavDialog = styled.dialog`
    display: block;
    padding: 0;
    margin: 0;
    justify-self: end;
    border: none;
    background-color: ${({ theme }) => theme.palette.white};
    position: static;

    @media (max-width: ${({ theme }) => theme.breakpoints.desktopMax}) {
        ${transition({}, "opacity", "visibility")};
        position: absolute;
        top: 0;
        right: 0;
        opacity: ${({ mobileMenuIsOpen }) => (mobileMenuIsOpen ? `1` : `0`)};
        visibility: ${({ mobileMenuIsOpen }) =>
            mobileMenuIsOpen ? `visible` : `hidden`};
        height: 100vh;
        width: 80%;
        max-width: 344px;
        z-index: ${({ overlayIsActive, theme }) =>
            overlayIsActive ? theme.zIndex.navWrapper : `inherit`};
        overflow-y: ${({ mobileMenuIsOpen }) =>
            mobileMenuIsOpen ? `scroll` : `hidden`};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        display: flex;
        align-items: center;
        flex: 1 1 auto;
    }
`;

const NavWrapper = styled.div`
    background-color: ${({ theme }) => theme.palette.white};

    @media (max-width: ${({ theme }) => theme.breakpoints.desktopMax}) {
        ${transition({}, "opacity", "visibility")};
        position: absolute;
        top: 0;
        right: 0;
        opacity: ${({ mobileMenuIsOpen }) => (mobileMenuIsOpen ? `1` : `0`)};
        visibility: ${({ mobileMenuIsOpen }) =>
            mobileMenuIsOpen ? `visible` : `hidden`};
        height: 100vh;
        width: 80%;
        max-width: 344px;
        z-index: ${({ overlayIsActive, theme }) =>
            overlayIsActive ? theme.zIndex.navWrapper : `inherit`};
        overflow-y: ${({ mobileMenuIsOpen }) =>
            mobileMenuIsOpen ? `scroll` : `hidden`};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        display: flex;
        align-items: center;
        flex: 1 1 auto;
    }
`;

const MainNav = styled.nav`
    padding-bottom: 140px;

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto;
        padding-bottom: 0;
    }
`;

const SecondaryNav = styled.div`
    display: none;

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        display: flex;
        align-items: center;
    }
`;

export const NavLinkWrapper = styled.div`
    ${transition({}, "box-shadow")};
    box-shadow: ${({ isNavOpened }) =>
        isNavOpened ? `0 2px 4px 2px rgba(0, 0, 0, 0.06)` : `none`};

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        position: relative;
    }
`;

const FooterNav = styled.li`
    border-top: ${({ theme }) => `1px solid ${theme.palette.black}`};
`;

export const MainNavLinkDefault = styled(BaseLink)`
    ${transition({}, "background-color", "text-decoration")};
    padding: 10px 20px;
    width: 100%;
    font-size: ${({ small }) => (small ? `1.6rem` : `2rem`)};
    font-weight: ${({ bold }) => (bold ? `800` : `400`)};
    color: ${({ theme, isActive }) =>
        isActive
            ? theme.palette.teal
            : theme.colors.typography.defaultLightBackground};
    display: ${({ hidden }) => (hidden ? "none" : "block")};

    &:hover,
    &:focus {
        text-decoration: underline;
        background-color: ${({ theme, small }) =>
            small ? "transparent" : theme.palette.grayFive};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        padding: 28px 15px;
    }
`;

const MobileNavWrapper = styled.div`
    ${transition({}, "background-color", "padding")};
    padding: ${({ mobileMenuIsOpen }) =>
        mobileMenuIsOpen ? "10px 20px" : "0"};
    display: flex;
    align-items: center;
    justify-content: flex-end;
    position: relative;
    background-color: ${({ theme, mobileMenuIsOpen }) =>
        mobileMenuIsOpen ? theme.palette.grayFive : theme.palette.white};

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        padding: 10px;
    }
`;

const MobileNavHeading = styled.h1`
    ${screenReaderText}
`;

const NavHeading = styled.h2`
    ${screenReaderText}
`;

const MainNavList = styled.ul`
    display: flex;
    list-style: none;
`;

const MobileMenuToggle = styled(IconOnlyButton)`
    display: none;

    @media (max-width: ${({ theme }) => theme.breakpoints.desktopMax}) {
        display: block;
        font-size: 2.2rem;
        color: ${({ theme }) => theme.colors.typography.defaultLightBackground};
    }
`;

const SkipLink = styled(Button)`
    position: absolute;
    top: 0;
    left: 0;
    font-size: 1.4rem;
    padding: 2px;
    color: ${({ theme }) => theme.palette.purple};
    opacity: 0;
    cursor: default;
    background-color: transparent;
    font-weight: 500;

    &:focus {
        opacity: 1;
        background-color: transparent;
    }
    &:hover {
        background-color: transparent;
        color: ${({ theme }) => theme.palette.purple};
    }
`;

const MobileList = styled.ul``;

const Header = ({
    logo = null,
    mainMenuItems = [],
    searchPlaceholder = `I'm searching for`,
    searchLabel = "What are you looking for?",
    contactLink = null,
    urlPermutations = [],
}) => {
    const [state, dispatch] = useReducer(headerReducer, initHeaderState);
    const {
        skipToContentText,
        mainText,
        mobileNavigationText,
        closeNavigationText,
        mainNavigationText,
    } = useStringTranslation([
        { text: "Skip to content", handle: "skipToContentText" },
        { text: "Main", handle: "mainText" },
        { text: "Mobile Navigation", handle: "mobileNavigationText" },
        { text: "Main navigation", handle: "mainNavigationText" },
        { text: "Close navigation", handle: "closeNavigationText" },
    ]);

    const { wp } = useStaticQuery(graphql`
        query StaticHeaderQuery {
            wp {
                optionsPage {
                    globalOptions {
                        showOrderSampleCart
                        showShoppingCart
                    }
                }
            }
        }
    `);

    const globalOptions = wp?.optionsPage?.globalOptions || {};
    const showShoppingCart = globalOptions.showShoppingCart || false;
    const showOrderSampleCart = globalOptions.showOrderSampleCart || false;

    const {
        bodyIsScrollLocked,
        mobileMenuIsOpen,
        menuOpened,
        overlayIsActive,
        openedSubnavs,
    } = state;

    const ref = useRef();
    const navWrapperRef = useRef();
    const mainNavRef = useRef();
    const mobileNavRef = useRef();
    const mobileNavToggleRef = useRef();
    const finalLinkMobileRef = useRef();
    const mobileNavExitRef = useRef();
    const primaryFullWrapperRef = useRef();
    const getBasePath = useGetBasePath();

    const homeUrl = getBasePath("pagesBase")?.url || "/";

    const keyboardNavigation = useCallback(
        (keyboardEvent) => {
            if (isBrowser && (menuOpened || mobileMenuIsOpen)) {
                navigateMenuWithKeyboard({
                    keyboardEvent,
                    mobileMenuIsOpen,
                    finalLinkMobileRef,
                    mobileNavExitRef,
                    mobileNavRef,
                });
            }
        },
        [mobileMenuIsOpen, menuOpened],
    );

    useOnWindowResize(() => {
        if (isBrowser) {
            dispatch({ type: "HANDLE_RESIZE", windowSize: window.innerWidth });
        }
    }, false);

    useEffect(() => {
        const primaryFullWrapper =
            document.getElementById("primaryFullWrapper");
        primaryFullWrapperRef.current = primaryFullWrapper;
    }, []);

    useOnKeyPress(KEYCODE_ESCAPE, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        const primaryFullWrapper = primaryFullWrapperRef.current;
        if (primaryFullWrapper) {
            primaryFullWrapper.classList.remove("overlayIsActive");
            dispatch({ type: "CLOSE_OVERLAY" });
        }
        dispatch({ type: "HANDLE_ESCAPE_KEY" });
    });

    useOnKeyPress(KEYCODE_TAB, (keyboardEvent) => {
        keyboardNavigation(keyboardEvent);
    });

    useEffect(() => {
        if (bodyIsScrollLocked) {
            clearAllBodyScrollLocks();
            dispatch({ type: "REMOVED_SCROLL_LOCK" });
        }
    }, [bodyIsScrollLocked]);

    useEffect(() => {
        if (mobileMenuIsOpen) {
            disableBodyScroll(navWrapperRef.current);
        } else {
            clearAllBodyScrollLocks();
        }

        return () => {
            clearAllBodyScrollLocks();
        };
    }, [mobileMenuIsOpen]);

    useEffect(() => {
        const primaryFullWrapper = primaryFullWrapperRef.current;

        if (primaryFullWrapper) {
            if (overlayIsActive) {
                primaryFullWrapper.classList.add("overlayIsActive");
            } else {
                primaryFullWrapper.classList.remove("overlayIsActive");
            }
        }
    }, [overlayIsActive]);

    useEffect(() => {
        if (isBrowser) {
            adjustNavMenuFocus({
                menuOpened,
                mobileMenuIsOpen,
                openedSubnavs,
                mobileNavToggleRef,
                mainNavRef,
                mobileNavRef,
            });
        }
    }, [menuOpened, mobileMenuIsOpen, openedSubnavs]);

    return (
        <HeaderStyled {...{ mobileMenuIsOpen }}>
            <SkipLink
                id="skip-link"
                ariaLabel={skipToContentText}
                onClick={() => {
                    if (isBrowser) {
                        scrollToElement(
                            document.querySelector("#mainContent h1"),
                        );
                    }
                }}
                tabIndex="0"
                newTabOverride="sameTab"
            >
                {skipToContentText}
            </SkipLink>
            <ContentContainer {...{ ref }}>
                <Wrapper>
                    <Logo>
                        {logo && (
                            <ImageLink
                                image={{
                                    source: logo.sourceUrl,
                                    alt: logo.altText || logo.title,
                                    width: logo.width,
                                    height: logo.height,
                                }}
                                to={homeUrl}
                                onClick={() =>
                                    trackNavigationEvent(
                                        false,
                                        1,
                                        "Logo",
                                        state.isDesktop,
                                    )
                                }
                            />
                        )}
                    </Logo>
                    {mobileMenuIsOpen ? (
                        <MobileNavDialog
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="mobileNavLabel"
                            ref={navWrapperRef}
                            {...{ mobileMenuIsOpen, overlayIsActive }}
                        >
                            {!menuOpened ? (
                                <MobileNavWrapper {...{ mobileMenuIsOpen }}>
                                    <MobileMenuToggle
                                        tagRef={mobileNavExitRef}
                                        screenReaderText={closeNavigationText}
                                        onClick={() =>
                                            dispatch({
                                                type: "HANDLE_MOBILE_NAVIGATION_CLOSE",
                                            })
                                        }
                                        iconLibrary="fas"
                                        icon="times"
                                    />
                                </MobileNavWrapper>
                            ) : null}

                            <MainNav aria-label={mainText}>
                                <MobileNavHeading id="mobileNavLabel">
                                    {mobileNavigationText}
                                </MobileNavHeading>
                                <MobileList role="list">
                                    {mainMenuItems.map((item, index) => (
                                        <li
                                            key={`MobileMain-${item.linkNativeName ?? item.linkLabel}`}
                                        >
                                            <MainMobileNavigationItem
                                                {...item}
                                                {...{
                                                    index,
                                                    dispatch,
                                                    openedSubnavs,
                                                    clearAllBodyScrollLocks,
                                                    menuOpened,
                                                }}
                                                tagRef={mobileNavRef}
                                            />
                                        </li>
                                    ))}

                                    {contactLink ? (
                                        <FooterNav>
                                            <NavLinkWrapper>
                                                <MainNavLinkDefault
                                                    bold
                                                    to={contactLink.linkUrl}
                                                    onClick={() =>
                                                        trackNavigationEvent(
                                                            false,
                                                            1,
                                                            contactLink.linkLabel,
                                                            false,
                                                        ) &&
                                                        clearAllBodyScrollLocks()
                                                    }
                                                >
                                                    {contactLink.linkLabel}
                                                </MainNavLinkDefault>
                                            </NavLinkWrapper>
                                        </FooterNav>
                                    ) : null}

                                    {!isEmpty(urlPermutations) ? (
                                        <li>
                                            <MobileLanguageNav
                                                {...{
                                                    openedSubnavs,
                                                    dispatch,
                                                    menuOpened,
                                                }}
                                                label="Language"
                                                links={urlPermutations}
                                                tagRef={finalLinkMobileRef}
                                            />
                                        </li>
                                    ) : null}
                                </MobileList>
                            </MainNav>
                        </MobileNavDialog>
                    ) : (
                        <ErrorBoundary>
                            <NavWrapper {...{ mobileMenuIsOpen }}>
                                <MainNav aria-label={mainText}>
                                    <NavHeading id="main-nav-heading">
                                        {mainNavigationText}
                                    </NavHeading>
                                    <MainNavList aria-labelledby="main-nav-heading">
                                        {mainMenuItems.map((item, index) => (
                                            <MainNavigation
                                                key={`NavList-${item.linkNativeName ?? item.linkLabel}-${index}`}
                                                {...item}
                                                {...{
                                                    index,
                                                    dispatch,
                                                    openedSubnavs,
                                                }}
                                                tagRef={mainNavRef}
                                            />
                                        ))}
                                    </MainNavList>
                                </MainNav>

                                {contactLink || !isEmpty(urlPermutations) ? (
                                    <SecondaryNav>
                                        {!isEmpty(urlPermutations) ? (
                                            <LanguageDropdown
                                                {...{
                                                    dispatch,
                                                    openedSubnavs,
                                                }}
                                                links={urlPermutations}
                                            />
                                        ) : null}

                                        {contactLink ? (
                                            <NavLinkWrapper>
                                                <MainNavLinkDefault
                                                    small
                                                    to={contactLink.linkUrl}
                                                    onClick={() =>
                                                        trackNavigationEvent(
                                                            false,
                                                            1,
                                                            contactLink.linkLabel,
                                                        )
                                                    }
                                                >
                                                    {contactLink.linkLabel}
                                                </MainNavLinkDefault>
                                            </NavLinkWrapper>
                                        ) : null}
                                    </SecondaryNav>
                                ) : null}
                            </NavWrapper>

                            <MobileNavWrapper>
                                <HeaderSearch
                                    searchPlaceholder={searchPlaceholder}
                                    searchLabel={searchLabel}
                                />

                                {showShoppingCart || showOrderSampleCart ? (
                                    <ShoppingCart />
                                ) : null}

                                <MobileMenuToggle
                                    tagRef={mobileNavToggleRef}
                                    screenReaderText={mainNavigationText}
                                    onClick={() =>
                                        dispatch({
                                            type: "HANDLE_MOBILE_MENU_TOGGLE",
                                        })
                                    }
                                    iconLibrary="fas"
                                    icon="bars"
                                />
                            </MobileNavWrapper>
                        </ErrorBoundary>
                    )}
                </Wrapper>
            </ContentContainer>
        </HeaderStyled>
    );
};

Header.propTypes = {
    urlPermutations: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.object,
            ]),
            linkUrl: PropTypes.string,
            linkNativeName: PropTypes.string,
            linkTranslatedName: PropTypes.string,
            languageCode: PropTypes.string,
        }),
    ),
    contactLink: PropTypes.shape({
        linkLabel: PropTypes.string,
        linkUrl: PropTypes.string,
    }),
    logo: PropTypes.shape({
        sourceUrl: PropTypes.string,
        altText: PropTypes.string,
        title: PropTypes.string.isRequired,
        width: PropTypes.string,
        height: PropTypes.string,
    }),
    mainMenuItems: PropTypes.arrayOf(PropTypes.shape({})),
    searchPlaceholder: PropTypes.string,
    searchLabel: PropTypes.string,
};

export default Header;
